import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useMutation } from "@apollo/client";
import { IconButton, Box, Stack, Typography, TextField, Tooltip, Alert, styled, useTheme } from "@mui/material";
import { IconTrash } from "@tabler/icons-react";
import Scrollbar from "../../NavBar/sidebar/Scrollbar";
import { deleteNote } from "../../../gql/mutations/notes";
import { getAllNotes } from "../../../gql/queries/notes";
import ColorsCheckbox from "../../forms/form-elements/checkbox/Colors";
import useQuery from "../../../hooks/allQuery";
import DeleteWebsitePopup from "../../Modal/components/DeleteWebsitePopup";

const BtnWrapper = styled("Box")(({ theme }) => ({
  "& button": {
    color: theme.palette.text.primary,
    "&:focus": {
      border: "none",
      outline: "none",
    },
  },
}));

function NoteList({ setNoteDetails, allNotes, notesFilter, setNotesFilter }) {
  const { allQuery } = useQuery();
  const { userData } = useSelector(state => state.reducer);
  const [DELETE_NOTE] = useMutation(deleteNote, { fetchPolicy: "network-only" });
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedNoteId, setSelectedNoteId] = useState(null);
  // const [setSelectedNote] = useState(null);
  useEffect(() => {
    if (allNotes && allNotes.length > 0) {
      const currentNote = allNotes.find(note => note.id === selectedNoteId);
      if (currentNote) {
        // setSelectedNote(currentNote);
        setNoteDetails(currentNote);
      } else {
        const latestNote = allNotes[0];
        setSelectedNoteId(latestNote.id);
        // setSelectedNote(latestNote);
        setNoteDetails(latestNote);
      }
    }
  }, [allNotes]);
  const handleChange = ({ target: { value } }) => {
    setNotesFilter({
      ...notesFilter,
      searchOptions: {
        ...notesFilter.searchOptions,
        searchTerm: value,
      },
    });
  };

  const handleNoteClick = note => {
    setSelectedNoteId(note.id);
    // setSelectedNote(note);
    setNoteDetails(note);
  };

  const handleDeleteClick = () => {
    if (!selectedNoteId) {
      alert("Please select a note before deleting!");
      return;
    }
    setOpen(true);
  };

  const handleDeleteSubmit = async () => {
    if (!selectedNoteId) return;

    try {
      setLoading(true);
      await allQuery({
        QUERY: DELETE_NOTE,
        variables: { deleteNoteId: selectedNoteId },
        type: "delete",
        cacheQueryData: {
          cacheQuery: getAllNotes,
          variablesCache: {
            userId: userData.id,
            searchOptions: notesFilter.searchOptions,
          },
        },
      });

      console.log("Note deleted successfully!");
      setOpen(false);
      setSelectedNoteId(null);
      // setSelectedNote(null);
      setNoteDetails(null);
      if (allNotes.length > 1) {
        const latestNote = allNotes.find(note => note.id !== selectedNoteId) || allNotes[0];
        setSelectedNoteId(latestNote.id);
        // setSelectedNote(latestNote);
        setNoteDetails(latestNote);
      }
    } catch (error) {
      console.error("Failed to delete note:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Box p={3} px={2}>
        <TextField
          id="search"
          name="search"
          value={notesFilter.searchOptions.searchTerm}
          placeholder="Search Notes"
          inputProps={{ "aria-label": "Search Notes" }}
          size="small"
          type="search"
          variant="outlined"
          fullWidth
          onChange={handleChange}
        />

        <ColorsCheckbox setNotesFilter={setNotesFilter} />
        <Typography variant="h6" mb={0} mt={4} pl={1}>
          All Notes
        </Typography>
      </Box>

      <Box>
        <DeleteWebsitePopup open={open} setOpen={setOpen} onSubmit={handleDeleteSubmit} loading={loading} />

        <Scrollbar
          sx={{
            height: { sm: "100vh", lg: "calc(100vh - 300px)" },
            maxHeight: "700px",
            "& .simplebar-track.simplebar-vertical": {
              "& .simplebar-scrollbar": {
                "&::before": {
                  backgroundColor: theme.palette.primary.main,
                },
              },
            },
          }}
        >
          {allNotes && allNotes.length ? (
            allNotes.map(note => (
              <Box key={note.id} px={2}>
                <Box
                  p={2}
                  onClick={() => handleNoteClick(note)}
                  sx={{
                    position: "relative",
                    cursor: "pointer",
                    mb: 1,
                    transition: "0.1s ease-in",
                    transform: note.id ? "scale(1)" : "scale(0.95)",
                    backgroundColor: selectedNoteId === note.id ? "rgba(0, 123, 255, 0.2)" : `${note.color}.light`,
                  }}
                >
                  <Typography variant="h6" noWrap color={note.color + ".main"}>
                    {note.note}
                  </Typography>
                  <BtnWrapper>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="caption">{new Date(note.createdAt).toLocaleDateString()}</Typography>
                      <Tooltip title="Delete">
                        <IconButton aria-label="delete" size="small" onClick={handleDeleteClick}>
                          <IconTrash width={18} />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </BtnWrapper>
                </Box>
              </Box>
            ))
          ) : (
            <Box m={2} sx={{ "& .MuiAlert-root": { justifyContent: "center", alignItems: "center" } }}>
              <Alert severity="error" variant="filled" sx={{ color: "white", textAlign: "center" }}>
                No Notes Found!
              </Alert>
            </Box>
          )}
        </Scrollbar>
      </Box>
    </>
  );
}

export default NoteList;
